import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { DICT } from "./i18n";

const Ctx = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [lang, setLangState] = useState(localStorage.getItem("lf_lang") || "en");
  const [picklists, setPicklists] = useState({});
  const [overrides, setOverrides] = useState({});

  const t = useCallback((key) => {
    const o = overrides[lang];
    if (o && o[key] != null && o[key] !== "") return o[key];
    return DICT[lang]?.[key] || DICT.en[key] || key;
  }, [lang, overrides]);
  const setLang = (l) => { setLangState(l); localStorage.setItem("lf_lang", l); };

  const reloadPicklists = useCallback(() => {
    api.get("/config/picklists").then((r) => setPicklists(r.data || {})).catch(() => {});
  }, []);

  const plLabelFor = useCallback((opt) => {
    if (typeof opt === "string") return opt;
    const l = opt.labels || {};
    return l[lang] || l.en || opt.id;
  }, [lang]);

  // Structured picklist options for a key -> [{id, label}] (lang-aware, tolerant of legacy strings)
  const plOptions = useCallback((key) => {
    const raw = picklists[key] || [];
    return raw.map((o) => (typeof o === "string" ? { id: o, label: o } : { id: o.id, label: plLabelFor(o) }));
  }, [picklists, plLabelFor]);

  // Resolve a stored option id to its localized display label (falls back to the id)
  const plLabel = useCallback((key, id) => {
    if (id == null || id === "") return id;
    const raw = picklists[key] || [];
    for (const o of raw) {
      const oid = typeof o === "string" ? o : o.id;
      if (oid === id) return typeof o === "string" ? o : plLabelFor(o);
    }
    return id;
  }, [picklists, plLabelFor]);

  const reloadTranslations = useCallback(() => {
    api.get("/config/translations").then((r) => setOverrides(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (user) { reloadPicklists(); reloadTranslations(); }
  }, [user, reloadPicklists, reloadTranslations]);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("lf_token");
    setUser(false);
  };

  const isManager = user && ["admin", "sales_manager"].includes(user.role);
  const canWrite = user && user.role !== "read_only";
  const hasIntel = user && ["admin", "sales_manager", "sales_rep"].includes(user.role);
  const perms = (user && user.perms) || {};
  const canReassign = !!(perms.is_admin || isManager || perms.can_reassign);

  return (
    <Ctx.Provider value={{ user, setUser, checking, lang, setLang, t, picklists, reloadPicklists, plOptions, plLabel, overrides, reloadTranslations, logout, isManager, canWrite, hasIntel, perms, canReassign }}>
      {children}
    </Ctx.Provider>
  );
}

export const useApp = () => useContext(Ctx);
